/**
 * The options the shared graphql-ws client is built from.
 *
 * Split out of `client.ts` so the transport's reconnect behaviour can be driven
 * by a test against a fake socket: importing `client.ts` builds an Apollo
 * client, and the property worth testing here — that a socket failure is
 * retried rather than fatal — needs nothing of the sort. `client.ts` is the
 * only production caller and passes the real dependencies.
 */

import type { ClientOptions } from "graphql-ws";

import type { WsLiveness } from "@/graphql/wsLiveness";

export type WebSocketConnectionState = "disconnected" | "connecting" | "connected" | "reconnecting" | "error";

export const WS_CONFIG = {
  // Unbounded. graphql-ws resets its retry counter only on a *successful*
  // connect, so any finite budget is a budget for the lifetime of the page:
  // once spent, every subscription is permanently dead until a reload. With
  // the old budget of 12 a laptop asleep for ten minutes was enough (#2937).
  // The backoff below is capped, so unbounded means one handshake attempt per
  // `maxReconnectDelay` while the network is down, not a hot loop — and the
  // loop ends anyway once the last subscription unsubscribes.
  maxReconnectAttempts: Infinity,
  baseReconnectDelay: 1500,
  maxReconnectDelay: 45000,
  connectionTimeout: 15000,
  // Interval between client-sent Pings, and how long one may go unanswered
  // before the socket is treated as dead. See `wsLiveness.ts`.
  keepAlive: 30000,
  pongTimeout: 8000,
};

/** Exponential backoff with 25% jitter, capped at `maxReconnectDelay`. */
function calculateReconnectDelay(attempt: number): number {
  let delay = WS_CONFIG.baseReconnectDelay * Math.pow(2, attempt - 1);
  delay = Math.min(delay, WS_CONFIG.maxReconnectDelay);

  // Add jitter (25% of delay)
  const jitter = Math.random() * delay * 0.25;
  return Math.min(delay + jitter, WS_CONFIG.maxReconnectDelay);
}

export interface WsClientDeps {
  /** Where to connect. A function so the URL is read at connect time. */
  url: string | (() => string);
  /** Liveness supervisor: pong deadline, backoff release, socket replacement. */
  liveness: WsLiveness;
  /** Per-browser session id, forwarded in `connection_init` for log tagging. */
  getSessionId: () => string;
  /** Report a transport state transition. */
  onState: (state: WebSocketConnectionState, error?: string) => void;
  /** Diagnostics sink. */
  log: {
    debug: (message: string, ...args: unknown[]) => void;
    info: (message: string, ...args: unknown[]) => void;
    warn: (message: string, ...args: unknown[]) => void;
    error: (message: string, ...args: unknown[]) => void;
  };
  /** Whether to emit the chatty per-transition lines. Production stays quiet. */
  verbose: boolean;
  /** Test seam: a fake WebSocket implementation. */
  webSocketImpl?: ClientOptions["webSocketImpl"];
}

export function buildWsClientOptions(deps: WsClientDeps): ClientOptions {
  const { url, liveness, getSessionId, onState, log, verbose, webSocketImpl } = deps;
  let reconnectAttempts = 0;

  return {
    url,
    webSocketImpl,
    connectionParams: () => {
      // Forward the per-browser sid (issue #1447) so a future server-side
      // WS handler can MDC-tag subscription log lines the same way the
      // HTTP filter does.
      return { sessionId: getSessionId() };
    },
    // A socket that opens but never gets its `connection_ack` back is another
    // dead connection with no detector: graphql-ws disables this timeout by
    // default (0), and the pong deadline would not expose it for a full
    // keepAlive interval.
    connectionAckWaitTimeout: WS_CONFIG.connectionTimeout,
    retryAttempts: WS_CONFIG.maxReconnectAttempts,
    // graphql-ws asks this before every retry, and its default answer —
    // `isLikeCloseEvent` — makes anything that is not a CloseEvent fatal: it
    // throws past the retry budget entirely and errors every subscriber's sink,
    // permanently. A browser reports an abnormal socket failure by firing
    // `error` (a plain DOM Event with no `code`/`reason`) *before* `close`, and
    // graphql-ws denies the connect promise with whichever arrives first, so
    // the default makes every failed handshake and every network-change drop
    // unrecoverable without a page reload. That is the defect behind
    // "Chat: realtime connection dropped" followed by silence for hours.
    //
    // Answering `true` does not weaken the genuinely fatal cases:
    // `shouldRetryConnectOrThrow` throws on fatal close codes (4400 Bad
    // Request, 4401 Unauthorized, 4409 Subscriber Already Exists, ...) and
    // honours disposal *before* it consults this. All it changes is the
    // non-CloseEvent arm, which is exactly the arm that should retry.
    shouldRetry: () => true,
    retryWait: async (retries) => {
      reconnectAttempts = retries + 1;
      const delay = calculateReconnectDelay(reconnectAttempts);

      if (verbose) {
        log.debug(`[GraphQL WS] Retry attempt ${reconnectAttempts}, waiting ${Math.round(delay)}ms`);
      }

      // Cut the wait short when the network returns: a machine waking from
      // sleep would otherwise sit out the remainder of a 45s backoff before
      // even trying, which is most of the delay the user experiences.
      await liveness.waitForBackoff(delay);
    },
    on: {
      connecting: () => {
        liveness.handleSocketPresent();
        const isReconnect = reconnectAttempts > 0;
        onState(isReconnect ? "reconnecting" : "connecting");

        if (verbose) {
          log.debug("[GraphQL WS] Connecting...", isReconnect ? "(reconnect)" : "(initial)");
        }
      },
      connected: () => {
        liveness.handleSocketPresent();
        reconnectAttempts = 0;
        onState("connected");

        if (verbose) {
          log.info("[GraphQL WS] Connected successfully");
        }
      },
      // A Ping we send starts the pong deadline; the answering Pong clears it.
      // Without this pair graphql-ws pings forever into a dead socket.
      ping: (receivedFromServer) => liveness.handlePing(receivedFromServer),
      pong: (receivedFromServer) => liveness.handlePong(receivedFromServer),
      closed: (event) => {
        liveness.handleSocketGone();
        const wasClean = Boolean(event && (event as CloseEvent).wasClean);

        if (wasClean) {
          onState("disconnected");
          if (verbose) {
            log.debug("[GraphQL WS] Connection closed cleanly");
          }
        } else {
          // Will trigger retry
          onState("reconnecting", "Connection lost");
          if (verbose) {
            log.warn("[GraphQL WS] Connection closed unexpectedly, will retry");
          }
        }
      },
      error: (error) => {
        liveness.handleSocketGone();
        const errorMessage = error instanceof Error ? error.message : "Unknown WebSocket error";
        onState("error", errorMessage);

        if (verbose) {
          log.error("[GraphQL WS] Connection error:", error);
        }
      },
    },
    // Lazy connection - only connect when first subscription is made
    lazy: true,
    // Ping interval. The pong deadline that makes it meaningful lives in
    // `wsLiveness`; graphql-ws itself never times a Pong out.
    keepAlive: WS_CONFIG.keepAlive,
  };
}
